import { useEffect, useState, useMemo } from "react";
// import {
//   Box,
//   Typography,
//   IconButton,
//   Paper,
//   Container,
//   Grid,
//   CircularProgress,
// } from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import SettingsIcon from "@mui/icons-material/Settings";
import { useNavigate } from "react-router-dom";
import logo from "../images/scrapyard.svg";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import GroupIcon from "@mui/icons-material/Group";
import { useAuth } from "../contexts/AuthContext";
import DialpadIcon from "@mui/icons-material/Dialpad";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SellIcon from "@mui/icons-material/Sell";

const primaryButtons = [
  {
    label: "ซื้อ",
    subLabel: "ซื้อสินค้าเข้าคลัง",
    icon: <ShoppingCartIcon style={{ fontSize: 48 }} />,
    route: "/buy",
    permission: [],
    color: "bg-blue-600 text-white shadow-blue-200 hover:shadow-blue-300",
    hoverColor: "hover:bg-blue-700",
  },
  {
    label: "ขาย",
    subLabel: "ขายสินค้าจากคลังสินค้า",
    icon: <SellIcon style={{ fontSize: 48 }} />,
    route: "/sale",
    permission: [],
    color: "bg-orange-500 text-white shadow-orange-200 hover:shadow-orange-300",
    hoverColor: "hover:bg-orange-600",
  },
];

const secondaryButtons = [
  {
    label: "รายงาน",
    icon: <DescriptionOutlinedIcon style={{ fontSize: 32 }} />,
    route: "/reports",
    permission: ["report_purchase_items", "report_sale_items"],
    color: "bg-purple-50 text-purple-600",
  },
  {
    label: "จัดการลูกค้า",
    icon: <ManageAccountsIcon style={{ fontSize: 32 }} />,
    route: "/customers",
    permission: ["customer_manage"],
    color: "bg-green-50 text-green-600",
  },
  {
    label: "จัดการลูกค้าฝั่งขาย",
    icon: <ManageAccountsIcon style={{ fontSize: 32 }} />,
    route: "/sale-customers",
    permission: ["customer_manage"],
    color: "bg-teal-50 text-teal-600",
  },
  {
    label: "จัดการกลุ่มลูกค้า",
    icon: <GroupIcon style={{ fontSize: 32 }} />,
    route: "/customer_group",
    permission: ["customer_group_manage"],
    color: "bg-cyan-50 text-cyan-600",
  },
  // {
  //   label: "จัดการร้านค้า",
  //   icon: <SettingsIcon style={{ fontSize: 32 }} />,
  //   // route: "https://shop.saleng.tjl.co.th",
  //   route: "http://localhost:3001/product",
  //   external: true,
  //   permission: [],
  //   color: "bg-gray-100 text-gray-600",
  // },
  {
    label: "ใบเสนอราคา(PO)",
    icon: <DescriptionOutlinedIcon style={{ fontSize: 32 }} />,
    route: "/po_management",
    permission: ["quotation_manage"],
    color: "bg-indigo-50 text-indigo-600",
  },
  {
    label: "ใบเสนอราคาขาย(SO)",
    icon: <DescriptionOutlinedIcon style={{ fontSize: 32 }} />,
    route: "/so_management",
    permission: ["quotation_manage"],
    color: "bg-pink-50 text-pink-600",
  },
  {
    label: "ล็อกราคาสินค้า",
    icon: <LockPersonIcon style={{ fontSize: 32 }} />,
    route: "/lock_pricing",
    permission: [],
    color: "bg-red-50 text-red-600",
  },
  {
    label: "สต๊อกสินค้า",
    icon: <Inventory2OutlinedIcon style={{ fontSize: 32 }} />,
    route: "/stock",
    permission: ["inventory_view_stock"],
    color: "bg-yellow-50 text-yellow-600",
  },
  {
    label: "จัดการปุ่มซื้อขาย",
    icon: <DialpadIcon style={{ fontSize: 32 }} />,
    route: "/edit_button",
    permission: [],
    color: "bg-emerald-50 text-emerald-600",
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (auth !== null) {
      setIsLoading(false);
    }
  }, [auth]);

  const { filteredPrimary, filteredSecondary } = useMemo(() => {
    if (!auth) return { filteredPrimary: [], filteredSecondary: [] };

    const permMap = auth.permissions || {};

    const checkPerm = (btn) => {
      const keys = btn.permission || [];
      return keys.length === 0 || keys.some((k) => permMap?.[k] === true);
    };

    return {
      filteredPrimary: primaryButtons.filter(checkPerm),
      filteredSecondary: secondaryButtons.filter(checkPerm),
    };
  }, [auth]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center p-2 md:p-4">
      <div className="w-full max-w-6xl mx-auto flex flex-col items-center">

        {/* Logo Section */}
        <div className="mb-4 flex flex-col items-center animate-fade-in-down mt-2">
          <img
            src={logo}
            alt="Scrapyard Logo"
            className="h-16 w-auto mb-2 drop-shadow-sm transition-transform hover:scale-105 duration-500"
          />
          <h1 className="text-xl font-medium text-gray-500 text-center">
            เลือกระบบที่ต้องการใช้งาน
          </h1>
        </div>

        {/* Primary Actions (Buy/Sell) */}
        <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {filteredPrimary.map((btn, index) => (
            <div
              key={index}
              onClick={() => navigate(btn.route)}
              className={`relative overflow-hidden rounded-3xl p-6 cursor-pointer transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl group ${btn.color} shadow-lg`}
            >
              <div className="absolute top-0 right-0 p-6 opacity-20 transform translate-x-4 -translate-y-4 group-hover:scale-150 transition-transform duration-500">
                {btn.icon}
              </div>

              <div className="relative z-10 flex flex-col items-start gap-3 h-full justify-center">
                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl shadow-inner">
                  {btn.icon}
                </div>
                <div className="flex flex-col">
                  <span className="text-2xl font-black tracking-tight">{btn.label}</span>
                  <span className="text-white/80 text-base font-medium">{btn.subLabel}</span>
                </div>
              </div>

              <div className={`absolute inset-0 ${btn.hoverColor} opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-0`} />
            </div>
          ))}
        </div>

        {/* Section Divider */}
        <div className="w-full flex items-center gap-4 mb-4">
          <div className="h-px bg-gray-200 flex-1"></div>
          <span className="text-xs font-bold text-gray-400 uppercase tracking-widest bg-gray-50 px-2">
            เมนูจัดการอื่นๆ
          </span>
          <div className="h-px bg-gray-200 flex-1"></div>
        </div>

        {/* Secondary Actions */}
        <div className="flex flex-wrap justify-center gap-3 w-full px-2 md:px-0 pb-4">
          {filteredSecondary.map((btn, index) => (
            <div
              key={index}
              onClick={() => {
                if (btn.external) {
                  window.open(btn.route, "_blank");
                } else {
                  navigate(btn.route);
                }
              }}
              className="w-[calc(50%-0.375rem)] md:w-[calc(33.333%-0.75rem)] lg:w-[calc(20%-0.6rem)] bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col items-center justify-center cursor-pointer transition-all duration-300 hover:shadow-lg hover:border-blue-200 hover:-translate-y-1 group min-h-[120px]"
            >
              <div className={`w-12 h-12 rounded-xl ${btn.color || 'bg-gray-50 text-gray-600'} flex items-center justify-center mb-2 transition-transform group-hover:scale-110 shadow-inner`}>
                {btn.icon}
              </div>
              <span className="text-gray-700 font-bold text-center whitespace-pre-line text-sm group-hover:text-blue-600 transition-colors">
                {btn.label}
              </span>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default HomePage;
